import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { createFeedEvent } from '../api/runs';
import { ApiError } from '../api/client';
import type { FeedEvent, FeedEventType } from '../api/types';
import ErrorAlert from './ErrorAlert';

const COMPOSER_TYPES: FeedEventType[] = ['announcement', 'countdown', 'reminder', 'alert'];

/** Sentinel for "no single team" — a broadcast. MUI reads an empty value as
 *  "nothing selected": the label drops into the field as a placeholder, and
 *  picking the option renders a blank field, because the displayed value *is*
 *  the empty string. A real value keeps both the label and the choice visible. */
const ALL_TEAMS = 'all';

/** Current local time as "HH:mm" — the countdown's date is always today, so the
 *  operator only picks a time of day, prefilled to now on load. */
function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

interface FeedComposerProps {
  orgSlug: string;
  runId: string;
  /** Teams in the run; when present, the operator can target a message at a
   *  single team instead of broadcasting to all (#239). */
  teams?: Array<{ id: string; name: string }>;
  onPosted?: (event: FeedEvent) => void;
}

const FeedComposer: React.FC<FeedComposerProps> = ({ orgSlug, runId, teams, onPosted }) => {
  const { t } = useTranslation('runs');

  const [eventType, setEventType] = useState<FeedEventType>('announcement');
  const [content, setContent] = useState('');
  const [targetTime, setTargetTime] = useState<string>(nowHHMM);
  const [targetTeamId, setTargetTeamId] = useState<string>(ALL_TEAMS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const eventContent: Record<string, unknown> = { text: content.trim() };
      if (eventType === 'countdown' && targetTime) {
        // Date is always today; the operator only sets the time of day.
        const [hh, mm] = targetTime.split(':').map(Number);
        const target = new Date();
        target.setHours(hh, mm, 0, 0);
        eventContent.target_time = target.toISOString();
      }
      const posted = await createFeedEvent(orgSlug, runId, {
        event_type: eventType,
        content: eventContent,
        target_team_id: targetTeamId === ALL_TEAMS ? null : targetTeamId,
      });
      setContent('');
      setTargetTime(nowHHMM());
      // Back to the broadcast. A sticky recipient is the dangerous default here:
      // the next message looks like it goes to everyone and quietly goes to
      // whichever team was picked last.
      setTargetTeamId(ALL_TEAMS);
      onPosted?.(posted);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={(e) => void handleSubmit(e)} sx={{ mb: 2 }}>
      {error && <ErrorAlert message={error} />}
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mb: 0.5,
              display: 'block'
            }}>
            {t('feedComposer.typeLabel')}
          </Typography>
          <ToggleButtonGroup
            value={eventType}
            exclusive
            onChange={(_, next: FeedEventType | null) => next && setEventType(next)}
            size="small"
          >
            {COMPOSER_TYPES.map((type) => (
              // The four type names alone do not say what they do differently
              // — "Erinnerung" vs "Ankündigung" is a guess until you have sent
              // one. Describe each on hover/focus. (#418, Toni T3-30)
              <Tooltip key={type} title={t(`feedComposer.typeHints.${type}`)}>
                <ToggleButton value={type}>
                  {t(`feedComposer.types.${type}`)}
                </ToggleButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
          {eventType === 'alert' && (
            // Alert keeps its urgent styling (Aaron's call, 2026-07-23) — so the
            // composer has to say what "urgent" costs, before it is sent to every
            // team in red. (#418, Toni T1-15)
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
              {t('feedComposer.alertHint')}
            </Alert>
          )}
        </Box>

        {teams && teams.length > 0 && (
          <TextField
            select
            label={t('feedComposer.targetTeam')}
            value={targetTeamId}
            onChange={(e) => setTargetTeamId(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value={ALL_TEAMS}>{t('feedComposer.allTeams')}</MenuItem>
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          label={t('feedComposer.content')}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          minRows={2}
          required
          fullWidth
        />

        {eventType === 'countdown' && (
          <TextField
            label={t('feedComposer.targetTime')}
            value={targetTime}
            onChange={(e) => setTargetTime(e.target.value)}
            type="time"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !content.trim()}
          >
            {t('feedComposer.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default FeedComposer;
